import uuid from "uuid";

const namespaced = true;

const state = {
    pages: [],
    pdfPages: [],
    paperTypes: [
        {
            title: "Stående A4",
            slug: "a4-standing",
            orientation: "portrait",
            format: "a4",
            width: 794,
            height: 1123
        },
        {
            title: "Liggande A4",
            slug: "a4-landscape",
            orientation: "landscape",
            format: "a4",
            width: 1122,
            height: 794
        },
        {
            title: "Stående A3",
            slug: "a3-standing",
            orientation: "portrait",
            format: "a3",
            width: 1122,
            height: 1587
        },
        {
            title: "Liggande A3",
            slug: "a3-landscape",
            orientation: "landscape",
            format: "a3",
            width: 1588,
            height: 1123
        }
    ],
    useInterpolationTitle: false
};

const getters = {
    pages: state => {
        return state.pages;
    },

    getPageById: state => id => {
        return state.pages.find(page => page.id == id);
    },

    interpolationSettings: state => {
        return state.interpolationSettings;
    },

    paperTypes: state => {
        return state.paperTypes;
    },

    pdfPages: state => {
        return state.pdfPages;
    },

    scales: state => {
        let scales = [];
        for (let i = 5; i <= 50; i++) {
            scales.push({
                title: "Skala 1:" + i + " 000",
                value: i * 1000
            });
        }
        return scales;
    },

    currentAnalyses: state => {
        let currentAnalyses = {};

        state.pages.forEach(page => {
            Object.keys(page.currentAnalyses).forEach(key => {
                if (!currentAnalyses.hasOwnProperty(key)) {
                    currentAnalyses[key] = 0;
                }
                currentAnalyses[key] += page.currentAnalyses[key];
            });
        });

        return currentAnalyses;
    },

    useInterpolationTitle: state => {
        return state.useInterpolationTitle;
    }
};

const actions = {
    ADD_NEW_PAGE({ commit, state }, payload) {
        let pageData = {
            id: uuid.v4(),
            paperType: state.paperTypes.find(
                type => type.slug == "a4-standing"
            ),
            interpolationSettings: {
                interpolationType: "ph",
                interpolationAlgorithm: 1,
                interpolationColorSmoothing: 0
            },
            scale: 0,
            scaleSettings: {},
            legendSettings: {},
            analysisTextSettings: {},
            footerSettings: {},
            headerSettings: {},
            currentAnalyses: {},
            footer: "",
            header: "",
            objects: [],
            additionalInfo: {
                top: null,
                left: null,
                entries: {
                    total_kalk_50_procent_cao: {
                        enabled: false,
                        content: null
                    }
                }
            },
            fontSize: 20,
            showText: true
        };

        commit("addPage", pageData);
    },

    ADD_NEW_PAGE_FROM_ACTIVE_PAGE({ commit, state }, payload) {
        // Json parse/stringify is used to clone the object.
        let page = JSON.parse(
            JSON.stringify(state.pages.find(page => page.id == payload))
        );
        page.id = uuid.v4();
        commit("addPage", page);
    },

    ADD_NEW_PAGE_FROM_TEMPLATE({ commit }, template) {
        let page = {
            id: uuid.v4(),
            paperType: template.settings.paperType,
            interpolationSettings: template.interpolationSettings,
            scaleSettings: template.settings.scaleSettings,
            legendSettings: template.settings.legendSettings,
            analysisTextSettings: template.settings.analysisTextSettings,
            footerSettings: template.settings.footerSettings,
            headerSettings: template.settings.headerSettings,
            scale: template.settings.scale,
            currentAnalyses: {},
            footer: "",
            header: "",
            objects: template.objects,
            additionalInfo: {
                top: null,
                left: null,
                entries: {
                    total_kalk_50_procent_cao: {
                        enabled: false,
                        content: null
                    }
                }
            },
            fontSize: template.settings.fontSize,
            showText: template.settings.showText
        };

        commit("addPage", page);
    },

    REMOVE_PAGE({ commit }, payload) {
        commit("removePage", payload);
    },

    SET_GLOBAL_SETTINGS({ commit, state }, payload) {
        state.pages.forEach(page => {
            commit("setPageSettings", { settings: payload, page: page });
        });
    },

    SET_PAGE_SETTINGS({ commit, state }, payload) {
        let page = state.pages.find(page => page.id == payload.id);
        commit("setPageSettings", { settings: payload, page: page });
    },

    SET_PAGE_HEADER({ commit }, payload) {
        commit("setPageHeader", payload);
    },

    SET_USE_INTERPOLATION_TITLE({ commit }, payload) {
        commit("setUseInterpolationTitle", payload);
    },

    SET_PAGE_FOOTER({ commit }, payload) {
        commit("setPageFooter", payload);
    },

    SET_CURRENT_ANALYSES({ commit }, payload) {
        commit("setCurrentAnalyses", payload);
    },

    UPDATE_PAGE_OBJECT({ commit }, payload) {
        commit("updatePageObject", payload);
    },

    UPDATE_PAGE_SETTING({ commit }, payload) {
        commit("updatePageSetting", payload);
    },

    REMOVE_OBJECT_FROM_PAGE({ commit }, payload) {
        commit("removeObjectFromPage", payload);
    },

    ADD_PDF_PAGE({ commit }, payload) {
        commit("addPdfPage", payload);
    },

    REMOVE_PDF_PAGES({ commit }) {
        commit("removePdfPages");
    }
};

// mutations
const mutations = {
    addPage(state, data) {
        state.pages.push(data);
    },

    removePage(state, id) {
        state.pages = state.pages.filter(page => page.id != id);
    },

    setPageSettings(state, payload) {
        let settings = payload.settings;
        let page = payload.page;
        if (settings.paperType) {
            page.paperType = state.paperTypes.find(
                type => type.slug == settings.paperType
            );
        }
        if (settings.interpolationType) {
            page.interpolationSettings.interpolationType =
                settings.interpolationType;
        }
        if (settings.interpolationAlgorithm) {
            page.interpolationSettings.interpolationAlgorithm =
                settings.interpolationAlgorithm;
        }
        if (typeof settings.interpolationColorSmoothing !== "undefined") {
            page.interpolationSettings.interpolationColorSmoothing =
                settings.interpolationColorSmoothing;
        }
        if (settings.fontSize) {
            page.fontSize = settings.fontSize;
        }
        if (typeof settings.showText !== "undefined") {
            page.showText = settings.showText;
        }

        if (typeof settings.scale !== "undefined") {
            page.scale = settings.scale;
        }

        if (settings.additionalInfo) {
            let entry = null;
            Object.keys(settings.additionalInfo.entries).forEach(key => {
                entry = settings.additionalInfo.entries[key];
                page.additionalInfo.entries[key].enabled = entry.enabled;
                page.additionalInfo.entries[key].content = entry.content;
            });
        }
    },

    setPageHeader(state, payload) {
        let page = state.pages.find(page => page.id == payload.id);
        page.header = payload.header;
    },

    setPageFooter(state, payload) {
        let page = state.pages.find(page => page.id == payload.id);
        page.footer = payload.footer;
    },
    setCurrentAnalyses(state, payload) {
        let page = state.pages.find(page => page.id == payload.id);
        page.currentAnalyses = payload.currentAnalyses;
    },

    setUseInterpolationTitle(state, payload) {
        state.useInterpolationTitle = payload.useInterpolationTitle;
    },

    updatePageObject(state, payload) {
        let page = state.pages.find(page => page.id == payload.id);
        let objectIndex = page.objects.findIndex(
            object => object.id == payload.object.id
        );

        if (objectIndex != -1) {
            page.objects[objectIndex] = payload.object;
        } else {
            page.objects.push(payload.object);
        }
    },

    updatePageSetting(state, { page_id, attribute, value }) {
        let page = state.pages.find(page => page.id == page_id);
        page[attribute] = value;
    },

    removeObjectFromPage(state, { page_id, object_id }) {
        let page = state.pages.find(page => page.id == page_id);
        let objectIndex = page.objects.findIndex(
            object => object.id == object_id
        );

        if (objectIndex >= 0) {
            page.objects.splice(objectIndex, 1);
        }
    },

    addPdfPage(state, page) {
        state.pdfPages.push(page);
    },

    removePdfPages(state) {
        state.pdfPages = [];
    }
};

export default {
    namespaced,
    state,
    getters,
    actions,
    mutations
};
